import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  buildPromotionAssetViewModel,
  buildPromotionShareMessage,
  mapPromotionKitError,
  useGetOrCreatePromotionLink,
  usePromotionAnalytics,
  useTranslation,
  canUseEntitlementGate,
  type EntitlementGateResult,
  type EntitlementSnapshot,
  type PromotionLinkWithUrl,
  type PromotionPublicUrlConfig,
  type PromotionTarget,
  type PromotionTargetsPage,
  type SupportedLocale,
  type TranslationKey,
} from '@dukanti/shared';
import { Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { PromotionAnalyticsSummaryCard } from './promotion-analytics-summary';
import { PromotionLocaleSelector } from './promotion-locale-selector';
import { PromotionPosterPreview } from './promotion-poster-preview';
import { PromotionSharePanel } from './promotion-share-panel';
import { PromotionStoryPreview } from './promotion-story-preview';
import { PromotionTargetPicker } from './promotion-target-picker';
import { downloadPromotionPoster, downloadPromotionStory } from '../lib/promotion-kit-downloads';
import { printPromotionKitPoster } from '../lib/promotion-kit-print';

export function PromotionKitPage(props: {
  readonly targetsPage: PromotionTargetsPage;
  readonly entitlement?: EntitlementSnapshot | null;
  readonly paidToolsGate?: EntitlementGateResult | null;
  readonly analyticsGate?: EntitlementGateResult | null;
  readonly publicUrlConfig: PromotionPublicUrlConfig;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const [messageLocale, setMessageLocale] = useState<SupportedLocale>(lang);
  const [selectedTarget, setSelectedTarget] = useState<PromotionTarget | null>(null);
  const [promotionLink, setPromotionLink] = useState<PromotionLinkWithUrl | null>(null);
  const createPromotionLinkMutation = useGetOrCreatePromotionLink(supabase);
  const createPromotionLinkRef = useRef(createPromotionLinkMutation.mutateAsync);
  const tRef = useRef(t);
  const missingGate = {
    allowed: false,
    messageKey: 'admin.entitlements.promotionKitBlocked' as TranslationKey,
  };
  const paidToolsGate =
    props.paidToolsGate !== undefined
      ? (props.paidToolsGate ?? missingGate)
      : props.entitlement === undefined
        ? { allowed: true, messageKey: null }
        : props.entitlement
          ? canUseEntitlementGate(props.entitlement, 'promotion_kit_paid_tools')
          : missingGate;
  const analyticsGate =
    props.analyticsGate !== undefined
      ? (props.analyticsGate ?? missingGate)
      : props.entitlement === undefined
        ? { allowed: true, messageKey: null }
        : props.entitlement
          ? canUseEntitlementGate(props.entitlement, 'promotion_analytics')
          : missingGate;
  const canUsePaidTools = paidToolsGate.allowed;
  const analyticsQuery = usePromotionAnalytics({
    client: supabase,
    storeId: props.targetsPage.store.storeId,
    enabled: analyticsGate.allowed,
  });

  useEffect(() => {
    createPromotionLinkRef.current = createPromotionLinkMutation.mutateAsync;
  }, [createPromotionLinkMutation.mutateAsync]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const firstTarget = useMemo(
    () =>
      props.targetsPage.groups
        .flatMap((group) => group.targets)
        .find((target) => canUsePaidTools || target.type === 'store_home') ?? null,
    [canUsePaidTools, props.targetsPage.groups],
  );
  useEffect(() => {
    setSelectedTarget((current) =>
      current?.storeId === props.targetsPage.store.storeId ? current : firstTarget,
    );
  }, [firstTarget, props.targetsPage.store.storeId]);

  const publicStorefrontOrigin = props.publicUrlConfig.publicStorefrontOrigin;
  const selectedTargetKey = selectedTarget
    ? JSON.stringify([
        props.targetsPage.store.storeId,
        selectedTarget.storeId,
        selectedTarget.id,
        selectedTarget.type,
        selectedTarget.targetId,
        selectedTarget.publicPath,
        publicStorefrontOrigin,
      ])
    : null;

  useEffect(() => {
    if (!selectedTarget || !selectedTargetKey) {
      setPromotionLink(null);
      return;
    }
    setPromotionLink(null);
    let active = true;
    createPromotionLinkRef
      .current({
        storeId: props.targetsPage.store.storeId,
        target: selectedTarget,
        publicUrlConfig: { publicStorefrontOrigin },
      })
      .then((link) => {
        if (active) setPromotionLink(link);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setPromotionLink(null);
        toast.error(tRef.current(mapPromotionKitError(err)));
      });
    return () => {
      active = false;
    };
  }, [props.targetsPage.store.storeId, publicStorefrontOrigin, selectedTarget, selectedTargetKey]);

  const shareMessage = useMemo(() => {
    if (!selectedTarget || !promotionLink) return '';
    return buildPromotionShareMessage({
      locale: messageLocale,
      storeName: props.targetsPage.store.storeName,
      target: selectedTarget,
      publicUrl: promotionLink.publicUrl,
      t,
    });
  }, [messageLocale, promotionLink, props.targetsPage.store.storeName, selectedTarget, t]);

  const asset = useMemo(() => {
    if (!selectedTarget || !promotionLink) return null;
    return buildPromotionAssetViewModel({
      storeName: props.targetsPage.store.storeName,
      target: selectedTarget,
      publicUrl: promotionLink.publicUrl,
      locale: messageLocale,
    });
  }, [messageLocale, promotionLink, props.targetsPage.store.storeName, selectedTarget]);

  async function copyText(value: string, successKey: TranslationKey): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t(successKey));
    } catch {
      toast.error(t('admin.promotionKit.errors.clipboardFailed' as TranslationKey));
    }
  }

  const selectedTargetIsPaid = selectedTarget?.type !== 'store_home';
  const paidBlocked = selectedTargetIsPaid && !canUsePaidTools;
  const disabled = !promotionLink || !selectedTarget || paidBlocked;
  const visibleGroups = useMemo(
    () =>
      canUsePaidTools
        ? props.targetsPage.groups
        : props.targetsPage.groups
            .map((group) => ({
              ...group,
              targets: group.targets.filter((target) => target.type === 'store_home'),
              hasMore: false,
            }))
            .filter((group) => group.targets.length > 0),
    [canUsePaidTools, props.targetsPage.groups],
  );

  function assetDownloadCopy(currentAsset: NonNullable<typeof asset>) {
    return {
      ctaText: t(currentAsset.ctaKey),
      brandText: 'Dukanti',
    };
  }

  function handleDownloadPoster(): void {
    if (!asset) return;
    void downloadPromotionPoster(asset, assetDownloadCopy(asset)).catch(() => {
      toast.error(t('admin.promotionKit.errors.assetFailed' as TranslationKey));
    });
  }

  function handleDownloadStory(): void {
    if (!asset) return;
    try {
      downloadPromotionStory(asset, assetDownloadCopy(asset));
    } catch {
      toast.error(t('admin.promotionKit.errors.assetFailed' as TranslationKey));
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(280px,360px)_1fr]">
      <div className="space-y-5">
        <PromotionTargetPicker
          groups={visibleGroups}
          selectedTargetId={selectedTarget?.id ?? null}
          search={props.search}
          onSearchChange={props.onSearchChange}
          onSelect={(target) => {
            setSelectedTarget(target);
            setPromotionLink(null);
          }}
          onLoadMore={props.onLoadMore}
          hasMore={props.hasMore}
        />
        <PromotionLocaleSelector value={messageLocale} onChange={setMessageLocale} />
        {!canUsePaidTools ? (
          <div className="border-saffron/40 bg-saffron/10 rounded-lg border p-4 text-start text-sm">
            <p className="text-sand-800 font-medium">
              {t('admin.entitlements.upgradePrompt.title')}
            </p>
            <p className="text-sand-600 mt-1">
              {t(paidToolsGate.messageKey ?? 'admin.entitlements.promotionKitBlocked')}
            </p>
          </div>
        ) : null}
      </div>
      <div className="space-y-5">
        <PromotionSharePanel
          publicUrl={promotionLink?.publicUrl ?? null}
          message={shareMessage}
          disabled={disabled}
          onCopyUrl={() =>
            void copyText(promotionLink?.publicUrl ?? '', 'admin.promotionKit.toasts.urlCopied')
          }
          onCopyMessage={() =>
            void copyText(shareMessage, 'admin.promotionKit.toasts.messageCopied')
          }
        />
        {canUsePaidTools ? (
          <div className="grid gap-5 xl:grid-cols-2">
            <div>
              <PromotionPosterPreview asset={asset} />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!asset}
                  onClick={handleDownloadPoster}
                >
                  <Download className="size-4" aria-hidden />
                  {t('admin.promotionKit.actions.downloadPoster' as TranslationKey)}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!asset}
                  onClick={() => printPromotionKitPoster()}
                >
                  <Printer className="size-4" aria-hidden />
                  {t('admin.promotionKit.actions.printPoster' as TranslationKey)}
                </Button>
              </div>
            </div>
            <div>
              <PromotionStoryPreview asset={asset} />
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                disabled={!asset}
                onClick={handleDownloadStory}
              >
                <Download className="size-4" aria-hidden />
                {t('admin.promotionKit.actions.downloadStory' as TranslationKey)}
              </Button>
            </div>
          </div>
        ) : null}
        {analyticsGate.allowed ? (
          <PromotionAnalyticsSummaryCard analytics={analyticsQuery.data ?? null} />
        ) : null}
      </div>
    </div>
  );
}
